'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function OrijinalDolguAnlamaYontemleri() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma';
  const pageTitle = 'Orijinal Dolguyu Anlama Yöntemleri: Sahte Ürünlerden Nasıl Korunulur?';
  const pageDescription = 'Dolgu yaptırmadan önce kullanılan ürünün orijinal olduğunu nasıl anlarsınız? Sahte dolgu ürünlerinin farkı nedir, nelere dikkat edilmelidir?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Orijinal Dolguyu Anlama Yöntemleri', href: '/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Orijinal dolgu ürünleri ve güvenlik rehberi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Orijinal Dolguyu Anlama Yöntemleri: Sahte Ürünlerden Nasıl Korunulur?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>10 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Son yıllarda estetik işlemlere olan ilgi artarken, sahte dolgu ürünlerinin de piyasada çoğalması ciddi bir risk oluşturmuştur.
                Orijinal olmayan dolgular, enfeksiyon, doku hasarı, alerjik reaksiyon ve kalıcı deformiteye yol açabilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu nedenle, işlemden önce dolgunun orijinalliğini kontrol etmek, en az uygulayıcının uzmanlığı kadar önemlidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Orijinal Dolguyu Anlamanın Temel Yöntemleri
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 mb-4 font-medium">
                  💡 Kural basit: kutusu açılmayan, barkodu gösterilmeyen dolguya güvenme.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-purple-200">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="border border-purple-200 px-4 py-3 text-left font-semibold text-purple-900">Kontrol Noktası</th>
                        <th className="border border-purple-200 px-4 py-3 text-left font-semibold text-purple-900">Açıklama</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-purple-200 px-4 py-3 font-medium text-purple-900">Kutu üzerindeki barkod</td>
                        <td className="border border-purple-200 px-4 py-3 text-purple-800">Her orijinal üründe üreticiye özel barkod bulunur.</td>
                      </tr>
                      <tr className="bg-purple-25">
                        <td className="border border-purple-200 px-4 py-3 font-medium text-purple-900">Hologram etiketi</td>
                        <td className="border border-purple-200 px-4 py-3 text-purple-800">Işığa tutulduğunda renk değiştiren güvenlik hologramı yer alır.</td>
                      </tr>
                      <tr>
                        <td className="border border-purple-200 px-4 py-3 font-medium text-purple-900">Seri numarası (LOT)</td>
                        <td className="border border-purple-200 px-4 py-3 text-purple-800">Her dolgu kutusunun kendine ait üretim LOT numarası vardır.</td>
                      </tr>
                      <tr className="bg-purple-25">
                        <td className="border border-purple-200 px-4 py-3 font-medium text-purple-900">Son kullanma tarihi</td>
                        <td className="border border-purple-200 px-4 py-3 text-purple-800">Net, silinmemiş ve üreticiyle uyumlu olmalıdır.</td>
                      </tr>
                      <tr>
                        <td className="border border-purple-200 px-4 py-3 font-medium text-purple-900">Soğuk zincir etiketi</td>
                        <td className="border border-purple-200 px-4 py-3 text-purple-800">Ürün +2°C ile +25°C arası saklama koşullarında olmalıdır.</td>
                      </tr>
                      <tr className="bg-purple-25">
                        <td className="border border-purple-200 px-4 py-3 font-medium text-purple-900">Ambalaj açımı</td>
                        <td className="border border-purple-200 px-4 py-3 text-purple-800">Ürün hasta önünde açılmalı, etiketi hasta formuna yapıştırılmalıdır.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. En Sık Sahteciliğe Uğrayan Markalar
              </h2>
              
              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 mb-4 font-medium">
                  Bu markaların popülerliği nedeniyle piyasada taklitleri sıkça görülür.
                  Özellikle "online satış" veya "kayıtsız muayenehane" kaynaklı ürünlerden uzak durulmalıdır.
                </p>
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li><strong>Juvederm®</strong></li>
                  <li><strong>Restylane®</strong></li>
                  <li><strong>Teosyal®</strong></li>
                  <li><strong>Belotero®</strong></li>
                  <li><strong>Neauvia®</strong></li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Sahte Dolguların Belirtileri
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-orange-800">
                  <li>⚠️ Uygulama sonrası uzun süren kızarıklık</li>
                  <li>⚠️ Sert nodül veya kitle oluşumu</li>
                  <li>⚠️ Asimetri ve renk değişikliği</li>
                  <li>⚠️ Kalıcılığın birkaç hafta içinde kaybolması</li>
                  <li>⚠️ Ambalajda yabancı dil hataları</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Orijinal Dolguların Belgeleri
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Orijinal ürünlerin kutusunda mutlaka şu belgeler bulunur:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>✅ Üretici sertifikası (CE veya FDA logosu)</li>
                  <li>✅ Hekim bilgilendirme kitapçığı</li>
                  <li>✅ Barkodlu "patient label" etiketi</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  Bu etiketin kopyası hasta dosyasına eklenmelidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Muayenehane Seçiminde Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-3 text-blue-800">
                  <li>Muayenehane Sağlık Bakanlığı onaylı olmalı</li>
                  <li>Dolgu uygulayıcısı hekim olmalı</li>
                  <li>Dolgu kutusu hasta önünde açılmalı</li>
                  <li>Ürünün fotoğrafı veya etiketi dosyada saklanmalı</li>
                </ul>
                <p className="text-blue-800 mt-3 font-medium">
                  💬 Orijinal dolgu, güvenli ve doğal sonuçların temelidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgumun orijinal olup olmadığını nasıl anlayabilirim?</h3>
                  <p className="text-gray-600">Kutu üzerindeki barkod, seri numarası ve hologramı kontrol edin.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. İnternetten dolgu almak güvenli mi?</h3>
                  <p className="text-gray-600">Hayır. Dolgular sadece medikal dağıtıcılar aracılığıyla muayenehanelere satılır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Sahte dolgu yapıldıysa ne olur?</h3>
                  <p className="text-gray-600">Enfeksiyon, doku ölümü ve nodül oluşumu riski yüksektir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Orijinal dolgu markaları hangileridir?</h3>
                  <p className="text-gray-600">Juvederm, Restylane, Teosyal, Belotero, Sculptra, Radiesse.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (U.S. Food and Drug Administration) – "Counterfeit Injectable Fillers Alert"</li>
                  <li>European CE Registry – "Authenticity Verification for Medical Injectables"</li>
                  <li>Harvard Health Publishing (2024) – "How to Verify Authentic Dermal Fillers"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Güvenliği ve Orijinallik Kılavuzu"</li>
                  <li>ISAPS Global Safety Report (2024) – "Counterfeit Risk in Aesthetic Medicine"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#orijinaldolgu</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#güvenlik</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#juvederm</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#teosyal</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#sahteurün</span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}